import { booleanAttribute, ChangeDetectionStrategy, Component, input } from '@angular/core';
import { SimIconComponent } from '@simOn/ui/sim-icon';

export type SimAttachmentTileType = 'image' | 'video' | 'document';

/**
 * 80x60 attachment tile used in scan tooltips: an image preview, a YouTube (video) icon
 * or a PDF (document) icon, with an optional count badge in the top-right corner.
 * `more` renders the overflow variant ("+2") instead of the content.
 *
 * @example
 * <sim-attachment-tile type="image" [src]="url" [count]="24" />
 * <sim-attachment-tile type="video" [count]="3" />
 * <sim-attachment-tile [more]="2" />
 */
@Component({
  selector: 'sim-attachment-tile',
  imports: [SimIconComponent],
  template: `
    @if (more(); as more) {
      <span class="sim-attachment-tile__more">+{{ more }}</span>
    } @else {
      <div class="sim-attachment-tile__content">
        @switch (type()) {
          @case ('image') {
            @if (src()) {
              <img class="sim-attachment-tile__image" [src]="src()" [alt]="alt()" />
            } @else {
              <sim-icon icon="image" sizeType="medium" />
            }
          }
          @case ('video') {
            <svg class="sim-attachment-tile__youtube" viewBox="0 0 34 24" aria-hidden="true">
              <rect width="34" height="24" rx="6" fill="#E62117" />
              <path d="M13.5 6.5v11l9-5.5z" fill="#fff" />
            </svg>
          }
          @case ('document') {
            <sim-icon icon="documents_file_pdf" sizeType="large" />
          }
        }
      </div>
      @if (count(); as count) {
        <span class="sim-attachment-tile__count">{{ count }}</span>
      }
    }
  `,
  styleUrl: './sim-attachment-tile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sim-attachment-tile',
    '[class.sim-attachment-tile--more]': '!!more()',
    '[class.sim-attachment-tile--stretch]': 'stretch()'
  }
})
export class SimAttachmentTileComponent {
  readonly type = input<SimAttachmentTileType>('image');
  /** Image url; used only for `type="image"`. */
  readonly src = input<string | null | undefined>(null);
  readonly alt = input<string>('');
  /** Number shown in the badge; hidden when 0/undefined. */
  readonly count = input<number | null | undefined>(null);
  /** Overflow variant: shows "+N" and nothing else. */
  readonly more = input<number | null | undefined>(null);
  /** Fills the available row width (flex: 1) instead of the fixed 80px. */
  readonly stretch = input(false, { transform: booleanAttribute });
}
